import React, {useState} from "react";
import {Card} from "../../components/Card";
import {OptiselModal} from "../../components/modal/OptiselModal";
import {Protected} from "../../layout/Protected";
import {IssueApiJson} from "./IssueApiJson";

/**
 * Karta zamykania sprawy jako zapytanie lub zamówienie. Oba warianty archiwizują sprawę
 * z wygenerowanym opisem ("Zamknięto jako ... nr ..."), a podany numer nadpisuje odpowiednią
 * referencję sprawy. Po zamknięciu użytkownik zostaje na stronie sprawy (przeładowanie),
 * gdzie baner archiwum udostępnia "Pobierz ZIP".
 */
export function IssueCloseAsCard(props: {
    issue: IssueApiJson,
    closeAsInquiry: (inquiryNumber: string) => Promise<void>,
    closeAsOrder: (wentechCaseNumber: string) => Promise<void>,
}) {
    const [openedDialog, setOpenedDialog] = useState<"none" | "inquiry" | "order">("none");
    const closeDialog = () => setOpenedDialog("none");

    return <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
        <Card header="Zamknij jako zamówienie lub zapytanie" additionalClassNames={"mt-3"}>
            <p className={"text-gray-600"}>
                Zamknięcie przenosi sprawę do archiwum i kończy jej cykl życia w aplikacji.
                Po zamknięciu dostępny będzie eksport ZIP z danymi sprawy i załącznikami.
            </p>
            <div className={"d-flex gap-2"}>
                <button className={"btn btn-sm btn-warning"} onClick={() => setOpenedDialog("inquiry")}>
                    <i className={"fas fa-question-circle"}/> Zamknij jako zapytanie
                </button>
                <button className={"btn btn-sm btn-warning"} onClick={() => setOpenedDialog("order")}>
                    <i className={"fas fa-file-invoice"}/> Zamknij jako zamówienie
                </button>
            </div>
        </Card>
        <IssueCloseAsModal
            opened={openedDialog === "inquiry"}
            onClose={closeDialog}
            header={"Zamknij jako zapytanie"}
            numberFieldLabel={"Numer zapytania (Wentech)"}
            initialNumber={props.issue.issueReferences.wentechInquiryNumber}
            performClose={props.closeAsInquiry}
        />
        <IssueCloseAsModal
            opened={openedDialog === "order"}
            onClose={closeDialog}
            header={"Zamknij jako zamówienie"}
            numberFieldLabel={"Numer sprawy (Wentech)"}
            initialNumber={props.issue.issueReferences.wentechCaseNumber}
            performClose={props.closeAsOrder}
        />
    </Protected>;
}

function IssueCloseAsModal(props: {
    opened: boolean,
    onClose: () => void,
    header: string,
    numberFieldLabel: string,
    initialNumber: string,
    performClose: (number: string) => Promise<void>,
}) {
    return <OptiselModal opened={props.opened} header={props.header} closeable={true} onClose={props.onClose}>
        {props.opened && <IssueCloseAsModalContent
            onClose={props.onClose}
            numberFieldLabel={props.numberFieldLabel}
            initialNumber={props.initialNumber}
            performClose={props.performClose}
        />}
    </OptiselModal>;
}

function IssueCloseAsModalContent(props: {
    onClose: () => void,
    numberFieldLabel: string,
    initialNumber: string,
    performClose: (number: string) => Promise<void>,
}) {
    const [number, setNumber] = useState(props.initialNumber);
    const [closing, setClosing] = useState(false);

    const submit = () => {
        setClosing(true);
        props.performClose(number.trim()).catch(() => {
            window.alert("Nie udało się zamknąć sprawy. Spróbuj ponownie.");
            setClosing(false);
        });
    };

    return <>
        <p>Sprawa zostanie zamknięta i przeniesiona do archiwum. Numer jest wymagany.</p>
        <div className="mb-3">
            <label className="form-label fw-bolder" htmlFor="issue-close-number">{props.numberFieldLabel}</label>
            <input id="issue-close-number" type="text" className="form-control" value={number}
                   onChange={e => setNumber(e.target.value)}/>
        </div>
        <div className={"d-flex gap-2 justify-content-end"}>
            <button className={"btn btn-secondary"} onClick={props.onClose} disabled={closing}>Anuluj</button>
            <button className={"btn btn-warning"} onClick={submit} disabled={number.trim() === "" || closing}>
                {closing ? <><i className="fas fa-spinner fa-spin me-2"/>Zamykanie…</> : "Zamknij sprawę"}
            </button>
        </div>
    </>;
}
